import { ContentState, convertToRaw, RichUtils } from "draft-js";
import { normaliseTableData } from "./tableUtils";
import { setBlockType } from "./draftUtils";

import { DOCUMENTS } from "../constants";

import applyFontStyle from "../components/CustomRevvToolbar/applyFontStyle";

const { DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;

export const getDraftFormFields = sectionData => {
  const data =
    sectionData instanceof ContentState
      ? convertToRaw(sectionData)
      : sectionData;

  const { entityMap = {} } = data;
  const fieldList = [];
  Object.values(entityMap).forEach(entity => {
    if (entity.type === "DYNAMICFIELD") {
      const { id, dynamicFieldId, selectedText } = entity.data;
      const field = {
        id,
        dynamicFieldId,
        selectedText,
        type: "DYNAMICFIELD",
      };
      fieldList.push(field);
    }
  });
  return fieldList;
};

export const getTableFormFields = sectionData => {
  const tableData = normaliseTableData(sectionData);
  const { data } = tableData;
  const fieldList = [];
  if (Array.isArray(data) && data.length > 0) {
    data.forEach(row => {
      const { data: rowData } = row;
      if (Array.isArray(rowData) && rowData.length > 0) {
        rowData.forEach(cell => {
          const { data: cellData } = cell;
          if (cellData instanceof Object) {
            fieldList.push(...getDraftFormFields(cellData.data));
          }
        });
      }
    });
  }
  return fieldList;
};

export const getSectionFormFields = section => {
  if (section) {
    const { id, type, data, clauseOptions } = section;
    if (type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
      const coverpageFieldLists = [];
      Object.keys(section.data.components).forEach(componentid => {
        const sec = section.data.components[componentid].content;
        coverpageFieldLists.push(...getSectionFormFields(sec));
      });
      return coverpageFieldLists;
    }
    if (type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
      const headerFooterFieldList = [];
      Object.keys(section.data.header.components).forEach(componentid => {
        const sec = section.data.header.components[componentid].content;
        headerFooterFieldList.push(...getSectionFormFields(sec));
      });
      Object.keys(section.data.footer.components).forEach(componentid => {
        const sec = section.data.footer.components[componentid].content;
        headerFooterFieldList.push(...getSectionFormFields(sec));
      });
      return headerFooterFieldList;
    }
    const formFields = [];
    let fields = [];
    // get form fields from dynamic field
    if (type === "HTML") {
      fields = getDraftFormFields(data);
    } else if (type === "Table") {
      fields = getTableFormFields(data);
    }
    formFields.push(...fields);

    // get form fields from clauseoptions
    if (clauseOptions) {
      const { id: optionID } = clauseOptions;
      formFields.push({
        id: optionID,
        dynamicFieldId: optionID,
        type: "CLAUSE",
      });
    }

    const fieldList = formFields.map(field => ({ ...field, sectionID: id }));
    return fieldList;
  }
  return [];
};

export const getFormFields = components => {
  const fieldList = [];
  Object.keys(components).forEach(componentId => {
    const section = components[componentId].content;
    fieldList.push(...getSectionFormFields(section));
  });
  return fieldList;
};

export const shouldSaveStateImmediately = editorState => {
  const changeTypes = {
    "change-inline-style": true, // when inline style is changed
    "remove-range": true, // when a chunk of text is removed
    "split-block": true, // when enter character
    "apply-entity": true, // when link, DF, etc is added
    "change-block-type": true, // H1, H2, etc.
    "spellcheck-change": true, // grammarly, etc.
    "insert-fragment": true, // paste content
    "backspace-character": false,
    "delete-character": false,
    "insert-characters": false,
  };
  return changeTypes[editorState.getLastChangeType()]
    ? changeTypes[editorState.getLastChangeType()]
    : false;
};

export const checkStylesInSelection = (editorState, type) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const styles = [];

  if (selection.isCollapsed()) {
    editorState
      .getCurrentInlineStyle()
      .forEach(style => style && styles.push(style));
  } else {
    let key = selection.getStartKey();
    let startOffset = selection.getStartOffset();
    const endKey = selection.getEndKey();
    const endOffset = selection.getEndOffset();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const lastRound = key === endKey;
      const block = contentState.getBlockForKey(key);
      const offsetEnd = lastRound ? endOffset : block.getLength();
      const characterList = block.getCharacterList();
      for (
        let offsetIndex = startOffset;
        offsetIndex < offsetEnd;
        offsetIndex += 1
      )
        characterList
          .get(offsetIndex)
          .getStyle()
          .forEach(style => style && styles.push(style));
      if (lastRound) break;
      key = contentState.getKeyAfter(key);
      startOffset = 0;
    }
  }
  const inlineStyles = Array.from(new Set(styles));
  let count = 0;
  if (Array.isArray(inlineStyles) && inlineStyles.length > 0) {
    inlineStyles.forEach(style => {
      if (typeof myVar === "string" && style.startsWith(type)) {
        count += 1;
      }
    });
  }

  return count;
};

export const applyStyleInEditor = (editorState, metaData, styleType) => {
  if (editorState.getSelection().isCollapsed()) {
    const count = checkStylesInSelection(editorState, styleType);
    if (count > 0) {
      return editorState;
    }

    const {
      FONTSIZE,
      FONTCOLOR,
      FONTFAMILY,
      FONTWEIGHT,
      BOLD,
      ITALIC,
      UNDERLINE,
      BLOCKTYPE,
    } = metaData;

    if (FONTFAMILY === undefined) {
      editorState = applyFontStyle(
        editorState,
        "FONTFAMILY_OPENSANS",
        "FONTFAMILY",
      );
      // Was creating some issues in DF stylings
      // if (BOLD === undefined && FONTWEIGHT === undefined)
      //   editorState = applyFontStyle(
      //     editorState,
      //     "FONTWEIGHT_REGULAR",
      //     "FONTWEIGHT",
      //   );
    }
    if (count === 0 && FONTSIZE && styleType === "FONTSIZE") {
      editorState = applyFontStyle(editorState, FONTSIZE, "FONTSIZE");
    }
    if (count === 0 && FONTCOLOR && styleType === "FONTCOLOR") {
      editorState = applyFontStyle(editorState, FONTCOLOR, "FONTCOLOR");
    }
    if (count === 0 && FONTFAMILY && styleType === "FONTFAMILY") {
      editorState = applyFontStyle(editorState, FONTFAMILY, "FONTFAMILY");
      if (FONTWEIGHT)
        editorState = applyFontStyle(editorState, FONTWEIGHT, "FONTWEIGHT");
    }
    if (count === 0 && BOLD && styleType === "BOLD") {
      editorState = RichUtils.toggleInlineStyle(editorState, styleType);
    }
    if (count === 0 && ITALIC && styleType === "ITALIC") {
      editorState = RichUtils.toggleInlineStyle(editorState, styleType);
    }
    if (count === 0 && UNDERLINE && styleType === "UNDERLINE") {
      editorState = RichUtils.toggleInlineStyle(editorState, styleType);
    }
    if (count === 0 && BLOCKTYPE && styleType === "BLOCKTYPE") {
      editorState = setBlockType(BLOCKTYPE, editorState);
    }

    return editorState;
  }
  return editorState;
};

export const sortContentState = rawContentState => {
  const { blocks, entityMap } = rawContentState;
  blocks.forEach(block => {
    // sort inline styles based on offset & style
    if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
      block.inlineStyleRanges.sort((a, b) => {
        if (a.offset === b.offset) {
          return a.style > b.style ? 1 : -1;
        }
        return a.offset > b.offset ? 1 : -1;
      });
    }

    // sort entity ranges by key
    if (block.entityRanges && block.entityRanges.length > 0) {
      block.entityRanges.sort((a, b) => (a.key > b.key ? 1 : -1));
    }
  });

  // sort entityMap by id
  if (entityMap && entityMap.length > 0) {
    entityMap.sort((a, b) => {
      if (!a.data || !a.data.id || !b.data || !b.data.id) {
        return 1; // move to end of array
      }
      return a.data.id > b.data.id ? 1 : -1;
    });
  }
  return rawContentState;
};



// WEBPACK FOOTER //
// ./src/utils/editorUtils.js